import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTelegramAuth } from './useTelegramAuth';
import {
  SupervisorAuthData,
  UseStepUpAuthOptions,
  UseStepUpAuthReturn,
  ActiveEmployee,
  PermissionKey
} from '../types/auth';

/**
 * Hook Universal de Step-up Authentication y Sudo Mode (PLAN-054)
 * 
 * Permite autorización expedita a 1 clic si la sesión activa ya cuenta con
 * permisos de supervisor o Superadmin (Sudo Mode), o delegación segura por
 * ingreso de PIN contra Notion y Redis Lockout si se opera en kiosco/ayudante.
 */
export function useStepUpAuth(options: UseStepUpAuthOptions): UseStepUpAuthReturn {
  const { requiredPermissions, actionName } = options;
  const { profile, token } = useTelegramAuth();

  const [supervisorEmployees, setSupervisorEmployees] = useState<ActiveEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lockoutRemainingSeconds, setLockoutRemainingSeconds] = useState<number | null>(null);

  // 1. Evaluar si la sesión activa cumple con los permisos (Sudo Mode)
  const hasActiveSupervisorSession = useMemo(() => {
    if (!profile) return false;
    // Superadmin tiene bypass universal
    if (profile.permissions?.includes('Superadmin')) return true;

    // Verificar si cumple cualquiera de los permisos requeridos
    return requiredPermissions.some((perm) =>
      profile.permissions?.includes(perm as PermissionKey)
    );
  }, [profile, requiredPermissions]);

  const activeSupervisor = useMemo<SupervisorAuthData | null>(() => {
    if (!hasActiveSupervisorSession || !profile) return null;
    return {
      id: String(profile.id),
      name: profile.name,
      authMethod: 'jwt',
      puestos: profile.puestos,
      permissions: profile.permissions,
      token: token || undefined
    };
  }, [hasActiveSupervisorSession, profile, token]);

  // 2. Cargar catálogo de supervisores activos para delegación por PIN
  const loadSupervisorEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const res = await fetch('/api/auth/employees');
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success' && Array.isArray(data.employees)) {
          // Filtrar preferentemente empleados con PIN configurado
          setSupervisorEmployees(data.employees);
        }
      }
    } catch (err: any) {
      console.warn('Error cargando empleados supervisores:', err);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    loadSupervisorEmployees();
  }, [loadSupervisorEmployees]);

  // Manejo del contador de bloqueo por 429
  useEffect(() => {
    if (lockoutRemainingSeconds === null || lockoutRemainingSeconds <= 0) return;
    const timer = setInterval(() => {
      setLockoutRemainingSeconds((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutRemainingSeconds]);

  // 3. Autorización Inmediata por Sesión Activa (Sudo Mode)
  const authorizeWithActiveSession = useCallback((): SupervisorAuthData => {
    if (!hasActiveSupervisorSession || !activeSupervisor) {
      const err = `La sesión activa no cuenta con permisos suficientes para ${actionName || 'esta acción'}.`;
      setError(err);
      throw new Error(err);
    }
    setError(null);
    return activeSupervisor;
  }, [hasActiveSupervisorSession, activeSupervisor, actionName]);

  // 4. Verificación de PIN Delegado contra /api/auth/pin-verify
  const verifySupervisorPin = useCallback(
    async (employeeId: string, pin: string) => {
      const cleanPin = pin.trim();
      if (!employeeId) {
        const msg = 'Debe seleccionar un supervisor del catálogo.';
        setError(msg);
        return { success: false, error: msg };
      }
      if (cleanPin.length < 4) {
        const msg = 'El PIN debe tener 4 dígitos numéricos.';
        setError(msg);
        return { success: false, error: msg };
      }

      setIsVerifying(true);
      setError(null);

      try {
        const res = await fetch('/api/auth/pin-verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employeeId,
            pin: cleanPin
          })
        });

        const data = await res.json();

        // 429 Lockout
        if (res.status === 429 || data.locked) {
          const waitSec = data.remainingSeconds || 600;
          setLockoutRemainingSeconds(waitSec);
          const msg = data.error || `Terminal bloqueada temporalmente por intentos fallidos. Reintente en ${Math.ceil(waitSec / 60)} min.`;
          setError(msg);
          setIsVerifying(false);
          return { success: false, error: msg, locked: true, remainingSeconds: waitSec };
        }

        // Validación Polimórfica: soporta status 'success' y 'authenticated'
        const isAuthSuccess = res.ok && (data.status === 'success' || data.status === 'authenticated');
        if (!isAuthSuccess) {
          const msg = data.error || 'PIN incorrecto o supervisor no autorizado.';
          setError(msg);
          setIsVerifying(false);
          return { success: false, error: msg };
        }

        // Extracción robusta de perfil: soporta profile y alias employee
        const supProfile = data.profile || data.employee;
        if (!supProfile) {
          const msg = 'Respuesta de servidor inválida: perfil no retornado.';
          setError(msg);
          setIsVerifying(false);
          return { success: false, error: msg };
        }

        // Comprobación de permisos en el perfil autenticado
        const permissions: string[] = supProfile.permissions || [];
        const isSuperadmin = permissions.includes('Superadmin');
        const hasReqPerm = isSuperadmin || requiredPermissions.some((p) => permissions.includes(String(p)));

        if (!hasReqPerm) {
          const msg = `El supervisor ${supProfile.name} no posee los permisos requeridos (${requiredPermissions.join(', ')}).`;
          setError(msg);
          setIsVerifying(false);
          return { success: false, error: msg };
        }

        const supervisorData: SupervisorAuthData = {
          id: String(supProfile.id),
          name: supProfile.name,
          authMethod: 'pin',
          supervisorPin: cleanPin,
          puestos: supProfile.puestos,
          permissions: supProfile.permissions,
          token: data.token
        };

        setIsVerifying(false);
        setError(null);
        return { success: true, supervisor: supervisorData };

      } catch (err: any) {
        const msg = `Error conectando con el servidor de autenticación: ${err.message}`;
        setError(msg);
        setIsVerifying(false);
        return { success: false, error: msg };
      }
    },
    [requiredPermissions]
  );

  const clearError = useCallback(() => {
    setError(null);
    setLockoutRemainingSeconds(null);
  }, []);

  return {
    hasActiveSupervisorSession,
    activeSupervisor,
    supervisorEmployees,
    loadingEmployees,
    isVerifying,
    error,
    lockoutRemainingSeconds,
    authorizeWithActiveSession,
    verifySupervisorPin,
    clearError
  };
}
